import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "../contexts/AppContext";
import api from "../lib/api";
import { FileDown, Search, ExternalLink, FileText } from "lucide-react";

const Forms = () => {
    const { t } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");

    useEffect(() => {
        api.get("/forms").then(({ data }) => setItems(data)).finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
    const filtered = useMemo(() => items.filter((i) => {
        const matchQ = !q || i.title.toLowerCase().includes(q.toLowerCase()) || (i.description || "").toLowerCase().includes(q.toLowerCase());
        const matchC = cat === "All" || i.category === cat;
        return matchQ && matchC;
    }), [items, q, cat]);

    return (
        <div data-testid="page-forms" className="container-x section-pad">
            <div className="max-w-3xl">
                <div className="chip bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))] mb-4">
                    <FileText size={14} /> {t.forms.chip}
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t.forms.title}</h1>
                <p className="mt-3 text-muted-foreground max-w-2xl">{t.forms.desc}</p>
            </div>

            <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center">
                <div className="relative flex-1">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input data-testid="forms-search" value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder={t.forms.searchPh}
                        className="w-full h-12 pl-11 pr-4 rounded-full border-2 border-border bg-background focus:border-[hsl(var(--royal))] focus:outline-none transition-colors" />
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                    {categories.map((c) => (
                        <button key={c} data-testid={`forms-cat-${c}`} onClick={() => setCat(c)}
                            className={`px-4 h-10 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border ${
                                cat === c ? "bg-[hsl(var(--royal))] text-white border-[hsl(var(--royal))]" : "bg-background border-border hover:bg-muted"
                            }`}>{c}</button>
                    ))}
                </div>
            </div>

            <div className="mt-10">
                {loading ? (
                    <div className="py-20 text-center text-muted-foreground">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="py-20 text-center text-muted-foreground">No forms found.</div>
                ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {filtered.map((f) => (
                            <div key={f.id} data-testid={`form-card-${f.id}`}
                                className="rounded-2xl border border-border bg-card p-5 hover:-translate-y-1 hover:shadow-lg transition-transform duration-200 flex flex-col">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))] flex items-center justify-center shrink-0">
                                        <FileText size={20} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{f.category}</div>
                                        <h3 className="mt-1 text-base font-semibold leading-snug">{f.title}</h3>
                                    </div>
                                </div>
                                {f.description && <p className="mt-3 text-sm text-muted-foreground">{f.description}</p>}
                                <div className="mt-auto pt-4 flex items-center justify-between">
                                    <span className="text-[11px] text-muted-foreground">{f.size_label || "PDF"}</span>
                                    <a data-testid={`form-download-${f.id}`} href={f.url} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--royal))] hover:gap-2.5 transition-all">
                                        {t.forms.download} <FileDown size={15} />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-14 rounded-3xl p-6 sm:p-8 border border-border bg-muted/30 flex flex-wrap items-center gap-4 justify-between">
                <div>
                    <div className="font-semibold">{t.forms.needHelpTitle}</div>
                    <p className="text-sm text-muted-foreground mt-1">{t.forms.needHelpDesc}</p>
                </div>
                <a href="https://wa.me/919000767404" target="_blank" rel="noreferrer" className="btn-royal">
                    <ExternalLink size={14} /> WhatsApp us
                </a>
            </div>
        </div>
    );
};

export default Forms;
